import React, { useRef, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
  createComment,
  commentTextChanged,
  createdComment,
  commentFocusChanged,
} from '../actions';
import { MAX_LANDING_PAGE_REPLY_LENGTH } from '../constants';
import PopupRecorder from '../components/Recorder/PopupRecorder';
import { supportsMediaRecorder } from '~/utils/BrowserSupport';
import { LandingPageIntent, ReduxState } from '~/types/redux/share';
import type { Reply } from '~/types/models/reply';
import {
  EmojiSelectorProps,
  ReplyPanel,
  ReplyPanelProps,
  ReplyPanelType,
} from '@vimily/bonjoro-ui-components-v2';

export interface ReplyPanelContainerProps
  extends Pick<ReplyPanelProps, 'inputRef' | 'onFocus' | 'className'> {
  onSendClick?: () => void;
  allowCompactBehavior?: boolean;
}

function ReplyPanelContainer({
  inputRef,
  onFocus,
  className,
  onSendClick,
  allowCompactBehavior = false,
}: ReplyPanelContainerProps) {
  const [focused, setFocused] = useState<boolean>(false);

  const [panelType, setPanelType] = useState<ReplyPanelType>(
    ReplyPanelType.Column
  );

  const input =
    inputRef ?? useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  const dispatch = useDispatch();

  const onEmojiSelect: EmojiSelectorProps['onSelect'] = (emoji) => {
    if (input.current) {
      const cursor = input.current.selectionStart as number;
      dispatch(
        commentTextChanged(
          input.current.value.slice(0, cursor) +
            emoji.native +
            input.current.value.slice(cursor)
        )
      );

      const newCursor = cursor + emoji.native.length;
      setTimeout(
        () => input?.current?.setSelectionRange(newCursor, newCursor),
        10
      );
      input.current.focus();
    }
  };

  const [isPopUpRecorderOpen, setIsPopUpRecorderOpen] = useState(false);
  const isBrowserSupported = supportsMediaRecorder();

  const { creating, commentValue, placeholder, lang, replyByVideo, intent } =
    useSelector((state: ReduxState) => ({
      error: state.comments.errorMessage,
      creating: state.comments.creating,
      commentValue: state.comments.value,
      placeholder: state.root.lang?.commentPlaceholder,
      lang: state.root.lang,
      replyByVideo: state.root.replyByVideo,
      intent: state.root.intent,
    }));

  useEffect(() => {
    if (!isPopUpRecorderOpen && intent === LandingPageIntent.ReplyByVideo) {
      setIsPopUpRecorderOpen(true);
    }
  }, [intent]);

  function createReplyForVideoReply() {
    return dispatch(createComment(input.current?.value ?? '', true));
  }

  function onVideoReplySuccess(reply: Reply) {
    dispatch(createdComment(reply));
  }

  // Set initial panel type
  useEffect(() => {
    setPanelType(
      allowCompactBehavior ? ReplyPanelType.Compact : ReplyPanelType.Column
    );
  }, [allowCompactBehavior]);

  // Set panel type base on focused state and allow compact behavior flag
  useEffect(() => {
    if (allowCompactBehavior) {
      setPanelType(focused ? ReplyPanelType.Column : ReplyPanelType.Compact);
    }
  }, [allowCompactBehavior, focused]);

  const onReplyPanelFocus = () => {
    dispatch(commentFocusChanged(true));
    onFocus?.();
    setFocused(true);
  };

  const onReplyPanelBlur = () => {
    dispatch(commentFocusChanged(false));
    setFocused(false);
  };

  const onInputChange = (input: string) => {
    dispatch(commentTextChanged(input));
  };

  const onSendClickHandler = (value: string) => {
    dispatch(createComment(value));
    input?.current?.focus();
    onSendClick?.();
  };

  const replyPanelProps: ReplyPanelProps = {
    id: 'reply-panel-container',
    inputId: 'reply-panel-input',
    value: commentValue,
    inputRef: input,
    className,
    panelType,
    disableSend: creating,
    disableInput: creating,
    onFocus: onReplyPanelFocus,
    onBlur: onReplyPanelBlur,
    onEmojiSelect,
    onReplyByVideoClick: () => setIsPopUpRecorderOpen(true),
    hideReplyByVideo: !(isBrowserSupported && replyByVideo),
    onInputChange,
    onSendClick: onSendClickHandler,
    replyByVideoLabel: lang?.replyByVideo,
    placeholder,
    inputClassName: '!p-2',
    emojiPopoverClassName: 'z-[999]',
    maxLength: MAX_LANDING_PAGE_REPLY_LENGTH,
    popoverPositions: ['top', 'right'],
  };

  return (
    <>
      {isPopUpRecorderOpen && (
        <PopupRecorder
          setOpen={setIsPopUpRecorderOpen}
          createReplyForVideoReply={createReplyForVideoReply}
          onVideoReplySuccess={onVideoReplySuccess}
        />
      )}
      <ReplyPanel {...replyPanelProps} />
    </>
  );
}

export default ReplyPanelContainer;
